import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import type { AssetListItem } from "../api/types";
import { VideoCard } from "../components/VideoCard";
import { useAuth } from "../auth/AuthContext";
import { useT } from "../i18n";

export function MyVideosPage() {
  const { user } = useAuth();
  const { t, locale } = useT();
  const [items, setItems] = useState<AssetListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.listAssets(50, undefined, undefined, { mine: true }, locale);
        if (!cancelled) setItems(res.data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  async function handleDelete(id: string, _title: string) {
    try {
      await api.deleteAsset(id);
      setItems((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (user?.role === "viewer") {
    return (
      <div className="px-4 py-10 text-center text-brand-muted">
        {t("myVideos.noPermission")}
      </div>
    );
  }

  const ready = items.filter((a) => a.status === "READY");
  const processing = items.filter((a) => a.status === "QUEUED" || a.status === "PROCESSING");
  const draft = items.filter((a) => a.status === "NEW");
  const failed = items.filter((a) => a.status === "FAILED");

  return (
    <div className="px-4 py-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">{t("myVideos.title")}</h1>
        <Link
          to="/upload"
          className="px-4 py-2 rounded-full bg-brand-red hover:bg-red-700 text-sm font-medium"
        >
          {t("myVideos.uploadNew")}
        </Link>
      </div>

      {error && <div className="text-red-400 mb-4">{error}</div>}

      {loading ? (
        <p className="text-brand-muted">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="text-center text-brand-muted py-20">
          <p className="text-lg">{t("myVideos.empty")}</p>
          <Link to="/upload" className="text-sm text-brand-red hover:underline mt-2 inline-block">
            {t("myVideos.uploadFirst")}
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {processing.length > 0 && (
            <Section title={t("myVideos.processing")} items={processing} onDelete={handleDelete} note={t("myVideos.processingNote")} />
          )}
          {ready.length > 0 && (
            <Section title={t("myVideos.published")} items={ready} onDelete={handleDelete} note={t("myVideos.publishedNote")} />
          )}
          {draft.length > 0 && (
            <Section title={t("myVideos.drafts")} items={draft} onDelete={handleDelete} note={t("myVideos.draftsNote")} />
          )}
          {failed.length > 0 && (
            <Section title={t("myVideos.failed")} items={failed} onDelete={handleDelete} note={t("myVideos.failedNote")} />
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  items,
  onDelete,
  note,
}: {
  title: string;
  items: AssetListItem[];
  onDelete: (id: string, title: string) => void;
  note?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-3">
        <h2 className="text-sm font-semibold text-brand-text">{title}</h2>
        <span className="text-xs text-brand-muted">({items.length})</span>
        {note && <span className="text-xs text-brand-muted">— {note}</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">
        {items.map((a) => (
          <VideoCard key={a.id} asset={a} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}